"use client";

import { useEffect, useState, useTransition } from "react";
import { EmojiPicker } from "@/components/emoji/emoji-picker";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { scheduleRouterRefresh } from "@/lib/client/app-router-refresh";
import { cn } from "@/lib/utils";
import type { ReactionSummary } from "@/types/database";

interface ReactionButtonProps {
	label: string;
	reactionSummary?: ReactionSummary[];
	userReactions?: string[];
	onToggle: (
		emoji: string,
		hasReaction: boolean,
	) => Promise<{ success: boolean }>;
	disabled?: boolean;
	className?: string;
	triggerLabel?: string;
}

function updateSummary(
	summary: ReactionSummary[],
	emoji: string,
	delta: 1 | -1,
) {
	const next = summary.map((reaction) => ({ ...reaction }));
	const existing = next.find((reaction) => reaction.reaction_type === emoji);

	if (existing) {
		existing.count += delta;
	} else if (delta > 0) {
		next.push({ reaction_type: emoji, count: 1 });
	}

	return next.filter((reaction) => reaction.count > 0);
}

export function ReactionButton({
	label,
	reactionSummary = [],
	userReactions = [],
	onToggle,
	disabled = false,
	className,
	triggerLabel,
}: ReactionButtonProps) {
	const [summary, setSummary] = useState(reactionSummary);
	const [selected, setSelected] = useState(userReactions);
	const [isPending, startTransition] = useTransition();
	const total = summary.reduce((count, reaction) => count + reaction.count, 0);

	useEffect(() => {
		setSummary(reactionSummary);
		setSelected(userReactions);
	}, [reactionSummary, userReactions]);

	const handleSelect = (emoji: string) => {
		const hasReaction = selected.includes(emoji);
		setSelected((current) =>
			hasReaction
				? current.filter((reaction) => reaction !== emoji)
				: [...current, emoji],
		);
		setSummary((current) =>
			updateSummary(current, emoji, hasReaction ? -1 : 1),
		);

		startTransition(async () => {
			const result = await onToggle(emoji, hasReaction);
			if (!result.success) {
				setSelected(userReactions);
				setSummary(reactionSummary);
				return;
			}
			scheduleRouterRefresh();
		});
	};

	return (
		<div className={cn("flex items-center gap-1", className)}>
			<EmojiPicker
				label={label}
				triggerLabel={triggerLabel}
				onSelect={handleSelect}
				disabled={disabled || isPending}
				selectedEmojis={selected}
				triggerClassName="text-content-secondary hover:bg-black/3 hover:text-content-primary dark:hover:bg-white/5"
			/>
			{summary.slice(0, 3).map((reaction) => {
				const active = selected.includes(reaction.reaction_type);
				return (
					<Button
						key={reaction.reaction_type}
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => handleSelect(reaction.reaction_type)}
						disabled={disabled || isPending}
						aria-pressed={active}
						className={cn(
							"h-8 gap-1 rounded-full px-2 text-sm",
							active
								? "bg-black/8 text-content-primary dark:bg-white/12"
								: "text-content-secondary hover:bg-black/3 dark:hover:bg-white/5",
						)}
					>
						<span aria-hidden>{reaction.reaction_type}</span>
						<Text as="span" size="xs" color="tertiary">
							{reaction.count}
						</Text>
					</Button>
				);
			})}
			{total > 0 && summary.length > 3 ? (
				<Text as="span" size="xs" color="tertiary">
					{total}
				</Text>
			) : null}
		</div>
	);
}
